import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	SwipeableDrawer,
	ListItemButton,
	Grid,
	ListItemText,
	ListItemIcon,
	ListItem,
	ListItemSecondaryAction,
	List as MuiList,
	SpeedDial,
	SpeedDialIcon,
	SpeedDialAction,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Global } from "@emotion/react";
import useStore from "../../store/store";
import transactionService from "../../services/transaction";
import style from "../../utils/style";
import constants from "../../utils/constants";
import utils from "../../utils/utils";
import { SearchBar, SwitchControl, ArrowUpChip } from "../../controls";
import AddTransaction from "../../components/AddTransaction";
import PageHeading from "../../components/PageHeading";
import TransactionDetails from "../../components/TransactionDetails";
import NoDataFound from "../../components/NoDataFound";
import { ArrowDown, ArrowUp } from "../../components/Arrows";

function Transaction() {
	let [keyword, setKeyword] = useState("");
	let [transactions, setTransactions] = useState([]);
	let [isOpen, setIsOpen] = useState(false);
	let [isDone, setIsDone] = useState(false);
	let [isDebit, setIsDebit] = useState(false);
	let [isCredit, setIsCredit] = useState(false);

	const navigate = useNavigate();

	let {
		token,
		setErrorMessage,
		setIsLoading,
		isLoggedIn,
		setCurrObj,
		toEdit,
		setToEdit,
		isAllTime,
	} = useStore((state) => state);

	const chevron = (
		<ion-icon name="chevron-forward-outline" color="medium"></ion-icon>
	);
	const both = (
		<ion-icon
			name="swap-vertical-outline"
			style={{ color: constants.PRIMARY }}
		></ion-icon>
	);

	const handleClick = (isDebit, isCredit) => {
		toggleDrawer();
		setIsDebit(isDebit);
		setIsCredit(isCredit);
	};

	const actions = [
		{
			icon: <ArrowUp fontSize={30} />,
			name: "Debit",
			onClick: () => {
				setToEdit(true);
				handleClick(true, false);
			},
		},
		{
			icon: <ArrowDown fontSize={30} />,
			name: "Credit",
			onClick: () => {
				setToEdit(true);
				handleClick(false, true);
			},
		},
		{
			icon: both,
			name: "Both",
			onClick: () => {
				setToEdit(true);
				handleClick(true, true);
			},
		},
	];

	useEffect(() => {
		if (!isLoggedIn) navigate("/login");
	}, []);

	useEffect(() => {
		reload();
	}, [keyword, isAllTime]);

	const reload = () => {
		setIsLoading(true);
		transactionService.reload(token, keyword, isAllTime).then((r) => {
			if (r.error) {
				setErrorMessage(r.error);
				setIsLoading(false);
				return;
			}
			if (r.data) {
				setTransactions(r.data);
				setIsLoading(false);
				setIsDone(true);
			}
		});
	};

	const toggleDrawer = () => setIsOpen(!isOpen);

	const Puller = styled(Box)(() => ({
		width: 30,
		height: 6,
		backgroundColor: "#ccc",
		borderRadius: 3,
		position: "absolute",
		top: 8,
		left: "calc(50% - 15px)",
	}));

	return (
		<>
			<SpeedDial
				ariaLabel="SpeedDial basic example"
				sx={{ position: "fixed", bottom: 80, right: 20 }}
				icon={<SpeedDialIcon />}
			>
				{actions.map((action) => (
					<SpeedDialAction
						key={action.name}
						icon={action.icon}
						onClick={action.onClick}
						sx={{ fontSize: 20 }}
					/>
				))}
			</SpeedDial>

			<Global
				styles={{
					".MuiDrawer-root > .MuiPaper-root": {
						height: toEdit ? `calc(100%)` : `calc(80%)`,
						overflow: "visible",
						borderTopLeftRadius: toEdit ? 0 : 10,
						borderTopRightRadius: toEdit ? 0 : 10,
					},
				}}
			/>

			<SwipeableDrawer
				anchor="bottom"
				open={isOpen}
				onClose={() => {
					setCurrObj(null);
					setIsDebit(false);
					setIsCredit(false);
					toggleDrawer();
				}}
				onOpen={() => {
					setToEdit(false);
					toggleDrawer();
				}}
				swipeAreaWidth={56}
				allowSwipeInChildren={true}
				disableSwipeToOpen={true}
				ModalProps={{
					keepMounted: true,
				}}
			>
				<Puller />

				{!isDebit && !isCredit && (
					<TransactionDetails
						reload={reload}
						toggleDrawer={toggleDrawer}
					/>
				)}
				{!isDebit && isCredit && (
					<AddTransaction
						toggleDrawer={toggleDrawer}
						reload={reload}
						isCredit={isCredit}
						setIsDebit={setIsDebit}
						setIsCredit={setIsCredit}
					/>
				)}
				{!isCredit && isDebit && (
					<AddTransaction
						toggleDrawer={toggleDrawer}
						reload={reload}
						isDebit={isDebit}
						setIsDebit={setIsDebit}
						setIsCredit={setIsCredit}
					/>
				)}
				{isCredit && isDebit && (
					<AddTransaction
						toggleDrawer={toggleDrawer}
						reload={reload}
						isDebit={isDebit}
						isCredit={isCredit}
						setIsDebit={setIsDebit}
						setIsCredit={setIsCredit}
					/>
				)}
			</SwipeableDrawer>

			<Grid container sx={style.container}>
				<Grid item xs={6} sx={{ mb: 1 }}>
					<PageHeading text="Transactions" />
				</Grid>

				<Grid item xs={6}>
					<SwitchControl />
				</Grid>

				<Grid item xs={12}>
					<SearchBar keyword={keyword} setKeyword={setKeyword} />
				</Grid>

				{isDone && transactions.length > 0 && (
					<Grid item xs={12} md={12}>
						<MuiList
							sx={{
								width: "100%",
								bgcolor: "background.paper",
								p: 0,
								m: 0,
							}}
						>
							{transactions.map((row, idx) => (
								<ListItem
									key={idx}
									onClick={() => {
										setCurrObj(row);
										setIsDebit(false);
										setIsCredit(false);
										toggleDrawer();
									}}
									sx={{ m: 0, p: 1, cursor: "pointer" }}
								>
									<ListItemSecondaryAction sx={{ right: 1 }}>
										{chevron}
									</ListItemSecondaryAction>
									<ListItemText
										sx={{ m: 0 }}
										primary={
											<span
												style={
													style.listItemPrimaryText
												}
											>
												{utils.formatToCurrency(
													row?.amount,
													"Rs. "
												)}
											</span>
										}
										secondary={
											<div
												style={{
													display: "flex",
													alignItems: "center",
													flexDirection: "row",
													gap: 5,
												}}
											>
												{row.toId && (
													<>
														<ArrowDown />{" "}
														<div
															style={
																style.listItemSecondaryText
															}
														>
															{row.toName}
														</div>
													</>
												)}
												{row.fromId && (
													<>
														<ArrowUp />{" "}
														<div
															style={
																style.listItemSecondaryText
															}
														>
															{row.fromName}
														</div>
													</>
												)}
											</div>
										}
									/>
								</ListItem>
							))}
						</MuiList>
						{transactions.length >= 12 && <ArrowUpChip />}
					</Grid>
				)}
				{isDone && transactions.length === 0 && (
					<Grid item xs={12}>
						<NoDataFound />
					</Grid>
				)}
			</Grid>
		</>
	);
}

export default Transaction;
